import initials from 'initials'
import { KeyRoundIcon, UserRoundIcon } from 'lucide-react'
import { useMemo, type CSSProperties, type ReactNode } from 'react'

import { type Actor as ActorData } from '@campai/lib-common/entities'
import { getActorImage, getActorKey } from '@campai/lib-common/utils/actor'
import { getRandomColor } from '@campai/lib-common/utils/misc'

import { BrandIcon } from '@/components/Brand'
import { AnonymousIcon } from '@/components/icons'
import { Avatar, AvatarFallback, AvatarImage, type AvatarSizeType } from '@/components/new/ui/avatar'
import { cn } from '@/components/new/lib/utils'


export const Actor = ({
    size,
    actor,
    className,
    children,
}: {
    size: AvatarSizeType
    actor: ActorData
    className?: string
    children?: ReactNode
}) => {
    const key = getActorKey(actor)
    const colorStyle = useMemo(() => {
        if (!key) return undefined
        const color = getRandomColor(key)
        return {
            '--actor-bg-light': color.bgLight,
            '--actor-bg-dark': color.bgDark,
            '--actor-border-light': color.borderLight,
            '--actor-border-dark': color.borderDark,
            '--actor-text-light': color.textLight,
            '--actor-text-dark': color.textDark,
        } as CSSProperties
    }, [key])
    const Icon = {
        user: UserRoundIcon,
        contact: UserRoundIcon,
        api: KeyRoundIcon,
        system: BrandIcon,
        guest: AnonymousIcon,
    }[actor.type]

    return (
        <Avatar
            size={size}
            style={colorStyle}
            className={cn(
                colorStyle && 'has-[[data-slot=avatar-fallback]]:after:hidden',
                className
            )}
        >
            <AvatarImage
                src={getActorImage(actor)?.resource}
                alt={actor.name}
            />
            <AvatarFallback
                className={cn(
                    colorStyle &&
                        'border border-[var(--actor-border-light)] bg-[var(--actor-bg-light)] text-[var(--actor-text-light)] dark:border-[var(--actor-border-dark)] dark:bg-[var(--actor-bg-dark)] dark:text-[var(--actor-text-dark)]'
                )}
            >
                {(actor.type === 'user' || actor.type === 'contact') && actor.name.trim() ? (
                    initials(actor.name).slice(0, 2).toUpperCase() || '-'
                ) : (
                    <Icon className="size-1/2" />
                )}
            </AvatarFallback>
            {children}
        </Avatar>
    )
}
